import { BubbleMenu, type BubbleMenuProps } from '@tiptap/react/menus'
import type { Editor } from '@tiptap/react'
import type { EditorFormattingState } from '../hooks/useEditorFormattingState'

interface EditorBubbleMenuProps {
  editor: Editor
  shouldShow: NonNullable<BubbleMenuProps['shouldShow']>
  formattingState: EditorFormattingState | null
  isEditingLink: boolean
  linkUrl: string
  setLinkUrl: (url: string) => void
  handleSetLink: () => void
  handleCancelLink: () => void
  handleStartEditLink: () => void
  appendTo?: () => HTMLElement
}

/**
 * Bubble menu component that appears when text is selected
 * Provides formatting controls and link editor
 */
export const EditorBubbleMenu = ({
  editor,
  shouldShow,
  formattingState,
  isEditingLink,
  linkUrl,
  setLinkUrl,
  handleSetLink,
  handleCancelLink,
  handleStartEditLink,
  appendTo,
}: EditorBubbleMenuProps) => {
  return (
    <BubbleMenu
      editor={editor}
      className="bubbleMenu"
      appendTo={appendTo ?? (() => document.body)}
      shouldShow={shouldShow}
      options={{
        placement: 'top',
        strategy: 'fixed',
        offset: 8,
      }}
    >
      {isEditingLink ? (
        <>
          <input
            type="url"
            className="linkInput"
            placeholder="Enter URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSetLink()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                handleCancelLink()
              }
            }}
            autoFocus
          />
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSetLink}
            title="Save Link"
            className="linkSaveButton"
          >
            ✓
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCancelLink}
            title="Cancel"
            className="linkCancelButton"
          >
            ✕
          </button>
        </>
      ) : (
        <>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={formattingState?.isBold ? 'isActive' : ''}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={formattingState?.isItalic ? 'isActive' : ''}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            className={formattingState?.isStrike ? 'isActive' : ''}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleStartEditLink}
            className={formattingState?.isLink ? 'isActive' : ''}
            title="Add Link"
          >
            🔗
          </button>
          <span className="bubbleDivider" />
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setParagraph().run()}
            className={formattingState?.isParagraph ? 'isActive' : ''}
            title="Paragraph"
          >
            P
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const { to } = editor.state.selection
              editor.chain().focus().toggleHeading({ level: 1 }).setTextSelection(to).run()
            }}
            className={formattingState?.headingLevel === 1 ? 'isActive' : ''}
            title="Heading 1"
          >
            H1
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const { to } = editor.state.selection
              editor.chain().focus().toggleHeading({ level: 2 }).setTextSelection(to).run()
            }}
            className={formattingState?.headingLevel === 2 ? 'isActive' : ''}
            title="Heading 2"
          >
            H2
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const { to } = editor.state.selection
              editor.chain().focus().toggleHeading({ level: 3 }).setTextSelection(to).run()
            }}
            className={formattingState?.headingLevel === 3 ? 'isActive' : ''}
            title="Heading 3"
          >
            H3
          </button>
        </>
      )}
    </BubbleMenu>
  )
}
